import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Save, X } from 'lucide-react';
import { toast } from '@/hooks/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { LoyaltyFormPreview } from './LoyaltyFormPreview';

interface LoyaltyFormUIText {
  headerTitle: string;
  headerSubtitle: string;
  pageTitle: string;
  pageSubtitle: string;
  nameLabel: string;
  namePlaceholder: string;
  emailLabel: string;
  emailPlaceholder: string;
  phoneLabel: string;
  phonePlaceholder: string;
  consentText: string;
  appleButtonText: string;
  googleButtonText: string;
  // Existing customer lookup text
  lookupSectionHeader?: string;
  lookupPhoneLabel?: string;
  lookupPhonePlaceholder?: string;
  lookupOrText?: string;
  lookupBarcodeLabel?: string;
  lookupButtonText?: string;
  lookupNotFoundMessage?: string;
  lookupFoundMessage?: string;
}

const DEFAULT_UI_TEXT: LoyaltyFormUIText = {
  headerTitle: "",
  headerSubtitle: "Collect points, gain rewards",
  pageTitle: "Get Your Loyalty Card",
  pageSubtitle: "Fill in your details to add this loyalty card to your phone",
  nameLabel: "Full Name",
  namePlaceholder: "John Doe",
  emailLabel: "Email Address",
  emailPlaceholder: "john@example.com",
  phoneLabel: "Phone Number",
  phonePlaceholder: "+1 (555) 000-0000",
  consentText: "I agree to terms",
  appleButtonText: "Add to Apple Wallet",
  googleButtonText: "Add to Google Wallet",
  // Existing customer lookup defaults
  lookupSectionHeader: "💡 Already have a loyalty card?",
  lookupPhoneLabel: "Phone number",
  lookupPhonePlaceholder: "+1 (555) 000-0000",
  lookupOrText: "or",
  lookupBarcodeLabel: "Or scan your barcode",
  lookupButtonText: "Find My Account",
  lookupNotFoundMessage: "We couldn't find your account. Please fill out the form below.",
  lookupFoundMessage: "✓ Account found! Please complete any missing information."
};

interface LoyaltyProgram {
  id: string;
  name: string;
  business_id: string;
  collect_email?: boolean;
  email_required?: boolean;
  collect_phone?: boolean;
  phone_required?: boolean;
  require_marketing_consent?: boolean;
  enable_existing_customer_lookup?: boolean;
  form_ui_text?: LoyaltyFormUIText;
  logo_url?: string; // Deprecated - kept for backward compatibility
  apple_logo_urls?: { '1x': string; '2x': string; '3x': string };
  google_logo_url?: string;
  form_preview_platform?: 'apple' | 'google';
}

interface LoyaltyFormCustomizerProps {
  program: LoyaltyProgram;
  businessName: string;
  onComplete?: () => void;
  onCancel?: () => void;
}

export const LoyaltyFormCustomizer: React.FC<LoyaltyFormCustomizerProps> = ({
  program,
  businessName,
  onComplete,
  onCancel
}) => {
  // Form settings
  const [collectEmail, setCollectEmail] = useState(program.collect_email ?? true);
  const [emailRequired, setEmailRequired] = useState(program.email_required ?? true);
  const [collectPhone, setCollectPhone] = useState(program.collect_phone ?? false);
  const [phoneRequired, setPhoneRequired] = useState(program.phone_required ?? false);
  // Marketing consent is always required
  const requireMarketingConsent = true;

  // Existing customer lookup
  const [enableLookup, setEnableLookup] = useState(program.enable_existing_customer_lookup ?? false);

  // Preview platform selection - defaults to saved value or 'apple'
  const [previewPlatform, setPreviewPlatform] = useState<'apple' | 'google'>(
    program.form_preview_platform || 'apple'
  );

  // UI text configuration
  const [uiText, setUiText] = useState<LoyaltyFormUIText>(() => {
    const savedText = program.form_ui_text || {};
    return {
      ...DEFAULT_UI_TEXT,
      ...savedText,
      // Use program name as default for headerTitle if not set
      headerTitle: savedText.headerTitle || program.name,
    };
  });

  const [saving, setSaving] = useState(false);

  const updateUIText = (field: keyof LoyaltyFormUIText, value: string) => {
    setUiText(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const { error } = await supabase
        .from('loyalty_programs')
        .update({
          collect_email: collectEmail,
          email_required: emailRequired,
          collect_phone: collectPhone,
          phone_required: phoneRequired,
          require_marketing_consent: true, // Always true
          enable_existing_customer_lookup: enableLookup,
          form_ui_text: uiText,
          form_preview_platform: previewPlatform
        })
        .eq('id', program.id);

      if (error) throw error;

      toast.success('Form settings saved successfully');

      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error('Error saving form settings:', error);
      toast.error('Failed to save form settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Main Form with right padding for preview */}
      <div className="lg:pr-96">
        <div className="space-y-4">
          {/* Field Collection Settings */}
          <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-2xl font-bold">Customize Form</CardTitle>
                <CardDescription>
                  Click any text in the preview to edit it directly
                </CardDescription>
              </div>
              {onCancel && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onCancel}
                  className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-6">
            <div className="space-y-2">
              <h3 className="text-base font-semibold">Form Fields</h3>
              <p className="text-sm text-muted-foreground">Choose which information to collect from customers</p>
            </div>

            {/* Email Collection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="collect-email">Collect Email Address</Label>
                  <p className="text-xs text-muted-foreground">
                    Ask for customer's email address
                  </p>
                </div>
                <Switch
                  id="collect-email"
                  checked={collectEmail}
                  onCheckedChange={setCollectEmail}
                />
              </div>

              {collectEmail && (
                <div className="ml-6 flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="email-required" className="text-sm">Make email required</Label>
                    <p className="text-xs text-muted-foreground">
                      User must provide email to continue
                    </p>
                  </div>
                  <Switch
                    id="email-required"
                    checked={emailRequired}
                    onCheckedChange={setEmailRequired}
                  />
                </div>
              )}
            </div>

            <Separator />

            {/* Phone Collection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="collect-phone">Collect Phone Number</Label>
                  <p className="text-xs text-muted-foreground">
                    Ask for customer's phone number
                  </p>
                </div>
                <Switch
                  id="collect-phone"
                  checked={collectPhone}
                  onCheckedChange={setCollectPhone}
                />
              </div>

              {collectPhone && (
                <div className="ml-6 flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="phone-required" className="text-sm">Make phone required</Label>
                    <p className="text-xs text-muted-foreground">
                      User must provide phone to continue
                    </p>
                  </div>
                  <Switch
                    id="phone-required"
                    checked={phoneRequired}
                    onCheckedChange={setPhoneRequired}
                  />
                </div>
              )}
            </div>

            <Separator />

            {/* Existing Customer Lookup */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="enable-lookup">Enable Existing Customer Lookup</Label>
                  <p className="text-xs text-muted-foreground">
                    Allow customers with existing cards to find their account
                  </p>
                </div>
                <Switch
                  id="enable-lookup"
                  checked={enableLookup}
                  onCheckedChange={setEnableLookup}
                />
              </div>

              {enableLookup && (
                <div className="ml-6 space-y-3">
                  <p className="text-xs text-muted-foreground">
                    Customers can search by phone number or scan their barcode to find their existing loyalty account and complete missing information.
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    💡 Lookup section text is customizable in the preview on the right
                  </p>

                  <Separator />

                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Lookup Messages</Label>

                    <div className="space-y-1.5">
                      <Label htmlFor="lookup-found-message" className="text-xs text-muted-foreground">
                        Success Message (when account is found)
                      </Label>
                      <textarea
                        id="lookup-found-message"
                        value={uiText.lookupFoundMessage || ''}
                        onChange={(e) => updateUIText('lookupFoundMessage', e.target.value)}
                        placeholder="✓ Account found! Please complete any missing information."
                        className="w-full px-3 py-2 text-xs border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-background text-foreground"
                        rows={2}
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="lookup-not-found-message" className="text-xs text-muted-foreground">
                        Not Found Message (when no account is found)
                      </Label>
                      <textarea
                        id="lookup-not-found-message"
                        value={uiText.lookupNotFoundMessage || ''}
                        onChange={(e) => updateUIText('lookupNotFoundMessage', e.target.value)}
                        placeholder="We couldn't find your account. Please fill out the form below."
                        className="w-full px-3 py-2 text-xs border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-background text-foreground"
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

          </CardContent>
          <CardContent className="pt-0">
            <div className="flex gap-3">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              {onCancel && (
                <Button
                  onClick={onCancel}
                  variant="outline"
                  disabled={saving}
                >
                  Cancel
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

          {/* Disclaimer */}
          <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <svg className="w-4 h-4 flex-shrink-0 text-blue-600 dark:text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-xs text-blue-800 dark:text-blue-300">
              <strong>Note:</strong> Both wallet buttons are shown in the preview for editing purposes. On the actual page, only one button appears based on the user's device (Apple Wallet for iOS, Google Wallet for Android/Desktop).
            </p>
          </div>
        </div>
      </div>

      {/* Fixed Preview on Right Side */}
      <div
        className="lg:fixed lg:top-6 lg:w-80 space-y-3 mt-8 lg:mt-0"
        style={{right: 'calc(24rem / 2 - 20rem / 2)'}}
      >
        <LoyaltyFormPreview
          collectEmail={collectEmail}
          emailRequired={emailRequired}
          collectPhone={collectPhone}
          phoneRequired={phoneRequired}
          requireMarketingConsent={requireMarketingConsent}
          enableLookup={enableLookup}
          uiText={uiText}
          businessName={businessName}
          programName={program.name}
          appleLogoUrl={program.apple_logo_urls?.['3x']}
          googleLogoUrl={program.google_logo_url}
          logoUrl={program.logo_url}
          previewPlatform={previewPlatform}
          onPreviewPlatformChange={setPreviewPlatform}
          editingEnabled={true}
          onUpdateUIText={updateUIText}
        />
      </div>
    </div>
  );
};
